import { ArrowLeft, ChevronRight, Home } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

export type Crumb = { label: string; to?: string };

/**
 * Where you are, and how to get back.
 *
 * Every page except the landing one had exactly one way out: the sidebar,
 * which is hidden below `md`. On a phone a dispatcher who opened a ticket from
 * the board had no way back to it except the browser's own button, and a
 * technician on My work had none at all.
 *
 * Two controls, because they answer different questions. **Back** goes to the
 * previous screen — the board you came from, not the board in general — and
 * uses history, so it lands where you actually were including the filter you
 * had set. The **breadcrumb** says where this page sits, which is the question
 * you ask when you arrived from a link and have no history to go back through.
 *
 * `backTo` is a fallback, not the default: opening a ticket URL directly gives
 * an empty history, and a Back button that does nothing is worse than none.
 */
export function PageNav({
  crumbs,
  backTo,
  backLabel = 'Back',
}: {
  crumbs: Crumb[];
  /** Where Back goes when there is no history to return to. */
  backTo?: string;
  backLabel?: string;
}) {
  const navigate = useNavigate();

  const goBack = () => {
    // `idx` is React Router's position in its own history stack. Zero means
    // this page is the first thing this tab opened, so there is nothing behind
    // it and the fallback is the only honest option.
    const atStart = (window.history.state?.idx ?? 0) === 0;
    if (atStart && backTo) navigate(backTo);
    else if (atStart) navigate('/');
    else navigate(-1);
  };

  return (
    <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
      <button
        type="button"
        onClick={goBack}
        className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-border px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {backLabel}
      </button>

      <nav aria-label="Breadcrumb" className="min-w-0">
        <ol className="flex min-h-[44px] flex-wrap items-center gap-1 text-sm text-muted-foreground">
          <li className="flex items-center gap-1">
            <Link to="/" className="-m-2 inline-flex min-h-[44px] items-center gap-1 p-2 hover:text-foreground">
              <Home className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="sr-only">Home</span>
            </Link>
          </li>

          {crumbs.map((crumb, index) => {
            const last = index === crumbs.length - 1;
            return (
              <li key={`${crumb.label}-${index}`} className="flex min-w-0 items-center gap-1">
                <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {crumb.to && !last ? (
                  <Link to={crumb.to} className="truncate hover:text-foreground">
                    {crumb.label}
                  </Link>
                ) : (
                  <span
                    className={`truncate ${last ? 'font-medium text-foreground' : ''}`}
                    aria-current={last ? 'page' : undefined}
                  >
                    {crumb.label}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </div>
  );
}
